import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  IonHeader,
  IonToolbar,
  IonButton,
  IonButtons,
  IonTitle,
  IonContent,
  IonIcon,
  IonAccordion,
  IonAccordionGroup,
  IonItem,
  IonLabel,
  IonToggle,
  ModalController,
} from '@ionic/angular/standalone';
import { SuperButtonComponent } from '@qid/super';
import { DisplayService } from '@qid/core';
import { Plan, PlanPricingModel, PlanService, PlanType } from '../../../plan';
import { PlanVisibility } from '../../../plan/lib/enum/plan-visibility.enum';
import { Organisation } from '../../../organisation';
import { Address } from '../../../../interface/address.inteface';
import { OrderMasterApiService } from '../../services/order-master-api.service';
import { OrderPriceBreakup } from '../../interface/order-price.breakup.interface';
import { DiscountType } from '../../enum/discount-type.enum';
import { isRazorpayPaymentMethod, MASTER_ORDER_PAYMENT_METHODS, PaymentMethod } from '../../enum/payment-method.enum';
import { PaymentMethodSelectorComponent } from '../../components/payment-method-selector/payment-method-selector.component';
import { CreateMasterOrderDto } from '../../dto/create-master-order.dto';

type Step = 'type' | 'plan' | 'pricing' | 'summary' | 'done';
type DiscountMode = 'amount' | 'percent';

/**
 * Master-admin wizard to create an order for an organisation.
 * Type → plan → pricing → summary → done (Razorpay link) or dismiss.
 */
@Component({
  selector: 'app-create-order-master',
  templateUrl: './create-order-master.page.html',
  styleUrls: ['./create-order-master.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SuperButtonComponent,
    PaymentMethodSelectorComponent,
    IonHeader,
    IonToolbar,
    IonButton,
    IonButtons,
    IonTitle,
    IonContent,
    IonIcon,
    IonAccordion,
    IonAccordionGroup,
    IonItem,
    IonLabel,
    IonToggle,
  ],
})
export class CreateOrderMasterPage implements OnInit {

  private static readonly GST_RATE = 0.18;
  private static readonly GSTIN_PATTERN =
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  @Input() selectedOrganisation: Organisation;
  /** When set, skips the plan-type step and starts on plan selection. */
  @Input() planType: PlanType | null = null;

  public readonly PlanType = PlanType;

  public readonly PaymentMethod = PaymentMethod;

  public step: Step = 'type';
  public plans: Plan[] = [];
  public selectedType: PlanType | null = null;
  public selectedPlan: Plan | null = null;
  public selectedPricing: PlanPricingModel | null = null;

  public discountMode: DiscountMode = 'amount';
  public discountInput: number | null = null;
  public autoRenews = false;
  public selectedPaymentMethod: PaymentMethod = PaymentMethod.RAZORPAY;
  public paymentMethodExclude: PaymentMethod[] = [PaymentMethod.RAZORPAY_SUBSCRIPTION];

  public billingName = '';
  public addressLine = '';
  public city = '';
  public state = '';
  public country = '';
  public zipCode = '';
  public gstin = '';

  public creating = false;
  public created = false;
  public paymentLink: string | null = null;
  public createdOrderId: string | null = null;

  /** True when opened with a known plan type (Upgrade / New Add-on). */
  get typeLocked(): boolean {
    return !!this.planType;
  }

  get gstinInvalid(): boolean {
    const value = (this.gstin || '').trim().toUpperCase();
    return !!value && !CreateOrderMasterPage.GSTIN_PATTERN.test(value);
  }

  get zipCodeInvalid(): boolean {
    const value = this.zipCode?.trim();
    return !!value && !/^\d{6}$/.test(value);
  }

  constructor(
    private modalController: ModalController,
    private planService: PlanService,
    private orderMasterApiService: OrderMasterApiService,
    private displayService: DisplayService,
  ) {
  }

  ngOnInit() {
    if (this.planType) {
      this.selectedType = this.planType;
      this.step = 'plan';
    }
    this.prefillBillingFromOrg();
    this.fetchPlans();
  }

  public get filteredPlans(): Plan[] {
    if (!this.selectedType) {
      return [];
    }
    return this.plans.filter((p) => p.type === this.selectedType);
  }

  public get publicPlans(): Plan[] {
    return this.filteredPlans.filter((p) => p.visibility === PlanVisibility.public);
  }

  public get otherPlans(): Plan[] {
    return this.filteredPlans.filter((p) => p.visibility !== PlanVisibility.public);
  }

  public get adminDiscountAmount(): number {
    if (!this.selectedPricing || !this.discountInput || this.discountInput <= 0) {
      return 0;
    }
    const subtotal = this.selectedPricing.amount || 0;
    const planDiscount = this.selectedPricing.discountAmount || 0;
    const amountBeforeTax = Math.max(0, subtotal - planDiscount);
    const raw = this.discountMode === 'percent'
      ? amountBeforeTax * (this.discountInput / 100)
      : this.discountInput;
    return Math.min(Math.max(0, raw), amountBeforeTax);
  }

  public get priceBreakup(): OrderPriceBreakup | null {
    if (!this.selectedPricing) {
      return null;
    }
    return this.computePriceBreakup(this.selectedPricing, this.adminDiscountAmount);
  }

  dismiss() {
    this.modalController.dismiss({
      created: this.created,
      link: this.paymentLink,
    });
  }

  async fetchPlans() {
    const plans = await this.planService.readAll();
    if (plans) {
      this.plans = plans;
    }
  }

  selectType(type: PlanType) {
    this.selectedType = type;
    this.selectedPlan = null;
    this.selectedPricing = null;
    this.discountInput = null;
    this.step = 'plan';
  }

  selectPlan(plan: Plan) {
    this.selectedPlan = plan;
    this.selectedPricing = null;
    this.discountInput = null;
    this.step = 'pricing';
  }

  selectPricing(pricing: PlanPricingModel) {
    this.selectedPricing = pricing;
    this.discountInput = null;
    this.step = 'summary';
  }

  onAutoRenewsChange(enabled: boolean) {
    this.autoRenews = enabled;
    this.selectedPaymentMethod = this.resolvePaymentMethod();
    this.paymentMethodExclude = this.autoRenews
      ? MASTER_ORDER_PAYMENT_METHODS.filter(
          (method) => method !== PaymentMethod.RAZORPAY_SUBSCRIPTION,
        )
      : [PaymentMethod.RAZORPAY_SUBSCRIPTION];
  }

  createOrder = async () => {
    if (!this.selectedPlan || !this.selectedPricing || !this.selectedOrganisation || this.creating) {
      return;
    }
    this.selectedPaymentMethod = this.resolvePaymentMethod();
    if (!this.selectedPaymentMethod) {
      await this.displayService.toast({ message: 'Select a payment method', color: 'warning' });
      return;
    }
    if (this.gstinInvalid) {
      await this.displayService.toast({ message: 'GSTIN format is invalid', color: 'warning' });
      return;
    }
    if (this.zipCodeInvalid) {
      await this.displayService.toast({ message: 'Pincode must be 6 digits', color: 'warning' });
      return;
    }

    this.creating = true;
    try {
      const discount = this.discountInput && this.discountInput > 0
        ? this.discountInput
        : undefined;
      const order = await this.orderMasterApiService.createMasterOrder({
        organisation: this.selectedOrganisation._id,
        items: [{ plan_id: this.selectedPlan._id }],
        period: (this.selectedPricing.timePeriod || this.selectedPricing.name || '').toLowerCase(),
        paymentMethod: this.selectedPaymentMethod,
        discount,
        discountType: discount != null
          ? (this.discountMode === 'percent' ? DiscountType.PERCENTAGE : DiscountType.FIXED)
          : undefined,
        ...this.buildBillingPayload(),
      });

      this.created = true;
      this.createdOrderId = order?._id || null;
      this.paymentLink = order?.razorpayShortUrl || null;

      if (isRazorpayPaymentMethod(this.selectedPaymentMethod) && this.paymentLink) {
        this.step = 'done';
        return;
      }

      const messages: Partial<Record<PaymentMethod, string>> = {
        [PaymentMethod.RAZORPAY]: 'Order created',
        [PaymentMethod.RAZORPAY_SUBSCRIPTION]: 'Order created',
        [PaymentMethod.UPI]: 'Order created — marked paid via UPI',
        [PaymentMethod.BANK_TRANSFER]: 'Order created — marked paid via bank transfer',
        [PaymentMethod.CASH]: 'Order created — marked paid via cash',
        [PaymentMethod.PAY_LATER]: 'Order created — pay later',
      };
      await this.displayService.toast({
        message: messages[this.selectedPaymentMethod] || 'Order created',
        color: 'success',
      });
      this.dismiss();
    } catch (e) {
      const message = typeof e === 'string' && e
        ? e
        : 'Could not create the order';
      await this.displayService.toast({ message, color: 'danger' });
    } finally {
      this.creating = false;
    }
  };

  goBack() {
    switch (this.step) {
      case 'plan':
        if (this.typeLocked) {
          return;
        }
        this.step = 'type';
        this.selectedType = null;
        break;
      case 'pricing':
        this.step = 'plan';
        this.selectedPlan = null;
        break;
      case 'summary':
        this.step = 'pricing';
        this.selectedPricing = null;
        this.discountInput = null;
        break;
    }
  }

  async copyLink() {
    if (!this.paymentLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(this.paymentLink);
      await this.displayService.toast({ message: 'Payment link copied', color: 'success' });
    } catch {
      await this.displayService.toast({ message: 'Copy failed — select and copy manually', color: 'warning' });
    }
  }

  /** Emails org admins the Razorpay pay link — same API as view-order payment reminder. */
  sendPaymentEmail = async () => {
    if (!this.createdOrderId) {
      return;
    }
    const ok = await this.displayService.confirm(
      'Send payment email?',
      'Emails organisation admins with the payment link for this order.',
      'Send',
    );
    if (!ok) {
      return;
    }
    try {
      const result = await this.orderMasterApiService.sendPaymentReminder(this.createdOrderId);
      const count = result.recipients?.length ?? 0;
      await this.displayService.toast({
        message: count
          ? `Payment email sent to ${count} recipient${count === 1 ? '' : 's'}`
          : 'Payment email sent',
        color: 'success',
      });
    } catch {
      await this.displayService.toast({ message: 'Could not send payment email', color: 'danger' });
    }
  };

  private prefillBillingFromOrg() {
    const org = this.selectedOrganisation;
    if (!org) {
      return;
    }
    const details = org.organisationDetails;
    this.billingName = org.name || '';
    this.addressLine = details?.addressLine || '';
    this.city = details?.city || '';
    this.state = details?.state || '';
    this.country = details?.country || '';
    this.zipCode = details?.pinCode || '';
    this.gstin = details?.gst || '';
  }

  /** Auto-renew → razorpay_subscription; otherwise never send subscription (razorpay is one-time). */
  private resolvePaymentMethod(): PaymentMethod {
    if (this.autoRenews) {
      return PaymentMethod.RAZORPAY_SUBSCRIPTION;
    }
    if (this.selectedPaymentMethod === PaymentMethod.RAZORPAY_SUBSCRIPTION) {
      return PaymentMethod.RAZORPAY;
    }
    return this.selectedPaymentMethod;
  }

  private buildBillingPayload(): Pick<CreateMasterOrderDto, 'billingName' | 'billingAddress' | 'gstin'> {
    const billingName = this.billingName?.trim();
    const gstin = this.gstin?.trim().toUpperCase();
    const addressLine = this.addressLine?.trim();
    const city = this.city?.trim();
    const state = this.state?.trim();
    const country = this.country?.trim();
    const zipCode = this.zipCode?.trim();

    const billingAddress: Address | undefined =
      addressLine || city || state || country || zipCode
        ? {
            addressLine: addressLine || undefined,
            dist: city || undefined,
            state: state || undefined,
            country: country || undefined,
            zipCode: zipCode || undefined,
          }
        : undefined;

    return {
      ...(billingName ? { billingName } : {}),
      ...(billingAddress ? { billingAddress } : {}),
      ...(gstin ? { gstin } : {}),
    };
  }

  private computePriceBreakup(
    pricingModel: PlanPricingModel,
    adminDiscount: number,
  ): OrderPriceBreakup {
    const round = (amount: number) => Math.round(amount * 100) / 100;
    const subtotal = pricingModel.amount || 0;
    const discountAmount = round((pricingModel.discountAmount || 0) + adminDiscount);
    const amountBeforeTax = Math.max(0, subtotal - discountAmount);
    return {
      subtotal: round(subtotal),
      discountAmount,
      amountBeforeTax: round(amountBeforeTax),
      taxAmount: round(amountBeforeTax * CreateOrderMasterPage.GST_RATE),
      payableAmount: round(amountBeforeTax * (1 + CreateOrderMasterPage.GST_RATE)),
    };
  }
}
